import React, { useState } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import apiClient from '@/utils/api';
import { setAuthToken, setUser, initiateGoogleAuth } from '@/utils/auth';
import { getPendingAction, clearPendingAction } from '@/utils/draftManager';
import { toast } from 'sonner';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  // Get redirect URL from query params or location state
  const redirectUrl = searchParams.get('redirect') || '/dashboard';
  const action = searchParams.get('action');
  const fromPath = location.state?.from;
  const pendingActionType = location.state?.pendingAction;
  const sessionExpired = searchParams.get('reason') === 'session_expired';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await apiClient.post('/auth/login', formData);
      setAuthToken(response.data.token);
      setUser(response.data.user);
      toast.success('Welcome back!');
      
      // Check for pending actions (draft restore)
      const pending = getPendingAction();
      if (pending) {
        clearPendingAction();
        navigate(pending.returnPath || '/dashboard');
        return;
      }

      // Handle different action types
      if (pendingActionType === 'list_property') {
        navigate('/list-property');
      } else if (fromPath) {
        navigate(fromPath);
      } else if (action === 'save_quote' || action === 'book_consultation') {
        navigate('/interior-design');
      } else {
        navigate(redirectUrl);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F5F0] py-32 px-6" data-testid="login-page">
      <div className="w-full max-w-md bg-white p-12 shadow-lg">
        <h1 className="text-4xl mb-2 text-center" data-testid="login-title">Welcome Back</h1>
        <p className="text-center text-gray-600 mb-8">Sign in to your ZenStudios account</p>
        
        {sessionExpired && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm p-3 mb-4" data-testid="session-expired-banner">
            Your session has expired. Please log in again.
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              className="rounded-none border-b border-t-0 border-l-0 border-r-0 px-0"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              data-testid="login-email-input"
            />
          </div>
          
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              className="rounded-none border-b border-t-0 border-l-0 border-r-0 px-0"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              data-testid="login-password-input"
            />
          </div>
          
          <div className="flex justify-end">
            <Link to="/forgot-password" className="text-xs text-green-700 hover:underline" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </div>
          
          <Button 
            type="submit" 
            className="w-full rounded-none bg-primary text-primary-foreground py-6"
            disabled={loading}
            data-testid="login-submit-btn"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
        
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with</span>
            </div>
          </div>
          
          <Button
            type="button"
            variant="outline"
            className="w-full mt-6 rounded-none py-6"
            onClick={initiateGoogleAuth}
            data-testid="login-google-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </Button>
        </div>
        
        <p className="mt-6 text-center text-sm text-gray-600">
          Don&apos;t have an account?{' '}
          <a href="/register" className="text-primary hover:underline" data-testid="login-register-link">
            Sign up
          </a>
        </p>
      </div>
    </div>
  );
};

export default Login;